<template>
    <div :class="wrapperClass" role="tablist" aria-label="Segmented toggle">
        <button
            v-for="(opt, idx) in normalizedOptions"
            :key="opt.value ?? idx"
            type="button"
            :class="segmentClass(opt)"
            :style="segmentStyle()"
            :aria-pressed="String(isSelected(opt))"
            role="tab"
            @click="onSelect(opt)">
            <span :class="labelClass(opt)">{{ opt.text }}</span>
        </button>
    </div>
</template>

<script>
export default {
    name: 'DesignSegmentedToggle',
    props: {
        modelValue: {
            type: [String, Number, Boolean, Object, null],
            default: null,
        },
        // [{ text: 'Admin', value: 'ADMIN' }, { text: 'Sub-admin', value: 'SUB_ADMIN' }]
        options: {
            type: Array,
            required: true,
            validator(arr){
                return Array.isArray(arr) && arr.every(o => typeof o === 'object' && 'text' in o);
            }
        },
        size: {
            type: String,
            default: 'MD', // MD | LG
        },
        // when true, stretches to full width; otherwise fits content like the mock
        fullWidth: {
            type: Boolean,
            default: false,
        },
        // fixed width per segment (px). When null, size by content
        segmentWidth: {
            type: [Number, String],
            default: 148, // Figma ~296px total inner width → 148px each
        },
        // when true, if modelValue is null, auto-select first option
        autoSelectFirst: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['update:modelValue', 'change'],
    data(){
        return {
            internalValue: this.modelValue,
        };
    },
    computed: {
        normalizedOptions(){
            return (this.options || []).map((o, i) => ({
                text: o.text,
                value: Object.prototype.hasOwnProperty.call(o, 'value') ? o.value : i,
            }));
        },
        wrapperClass(){
            // Inline pill container, natural width, fixed inner padding per Figma
            const widthClass = this.fullWidth ? 'flex w-full' : 'inline-flex';
            const heightClass = this.size === 'LG' ? 'h-11' : 'h-10'; // LG: 44px, MD: 40px
            const base = `${widthClass} items-center rounded-lg border border-[#D1D3D8] bg-white p-1 ${heightClass}`;
            return [base];
        },
    },
    watch: {
        modelValue(val){
            this.internalValue = val;
        },
        options: {
            handler(){
                // If current value is no longer valid, optionally select the first
                const exists = this.normalizedOptions.some(o => this.isEqual(o.value, this.internalValue));
                if (!exists && this.autoSelectFirst && this.normalizedOptions.length > 0) {
                    this.setValue(this.normalizedOptions[0].value, false);
                }
            },
            deep: true,
        },
    },
    mounted(){
        if (this.internalValue == null && this.autoSelectFirst && this.normalizedOptions.length > 0) {
            this.setValue(this.normalizedOptions[0].value, false);
        }
    },
    methods: {
        isEqual(a, b){
            try { return JSON.stringify(a) === JSON.stringify(b); } catch { return a === b; }
        },
        isSelected(opt){
            return this.isEqual(opt.value, this.internalValue);
        },
        setValue(val, emitChange = true){
            this.internalValue = val;
            this.$emit('update:modelValue', val);
            if (emitChange) this.$emit('change', val);
        },
        onSelect(opt){
            if (this.isSelected(opt)) return;
            this.setValue(opt.value, true);
        },
        segmentClass(opt){
            const base = 'px-5 select-none outline-0 transition-colors rounded-md inline-flex items-center justify-center text-center whitespace-nowrap';
            // Height only; typography is handled by index.css utility classes
            const sizing = this.size === 'LG' ? 'h-9' : 'h-8';
            const state = this.isSelected(opt)
                ? 'bg-primary-3 text-white'
                : 'bg-transparent text-[#ADB1BA] hover:bg-[#E8EBFA]';
            return [base, sizing, state].join(' ');
        },
        segmentStyle(){
            if (this.segmentWidth == null || this.segmentWidth === '' || this.segmentWidth === false) return null;
            const px = typeof this.segmentWidth === 'number' ? `${this.segmentWidth}px` : String(this.segmentWidth);
            return { width: px };
        },
        labelClass(opt){
            // Use design system typography defined in index.css
            return ['font-inter-body-1', 'truncate'].join(' ');
        },
    },
};
</script>

<style scoped>
</style>


